import type { DebugLog, TimelineStep } from "./LeadDebugPage.types";

const STATUS_LABELS: Record<string, string> = {
    RECEIVED: "Received",
    PROCESSED: "Processed",
    SENT: "Sent",
    PROCESS_FAILED: "Process Failed",
    DUPLICATED: "Duplicated",
    FAILED_TO_FETCH_DATA: "Failed to Fetch Data",
    SEND_FAILED: "Send Failed",
};

export function getStatusLabel(status: string): string {
    return STATUS_LABELS[status] ?? status;
}

export function getStatusColor(
    status: string,
): "success" | "error" | "primary" | "default" {
    switch (status) {
        case "SENT":
        case "PROCESSED":
        case "RECEIVED":
            return "success";
        case "PROCESS_FAILED":
        case "SEND_FAILED":
        case "FAILED_TO_FETCH_DATA":
            return "error";
        case "DUPLICATED":
            return "primary";
        default:
            return "default";
    }
}

/**
 * Parse received_at, processed_at, sent_at into timeline steps with relative ms from start.
 */
export function buildTimeline(log: DebugLog): TimelineStep[] {
    const received = log.received_at ? new Date(log.received_at).getTime() : 0;
    const processed = log.processed_at
        ? new Date(log.processed_at).getTime()
        : received;
    const sent = log.sent_at ? new Date(log.sent_at).getTime() : processed;

    const base = received;
    return [
        {
            label: "WEBHOOK RECV",
            key: "received",
            timestamp: log.received_at ?? "",
            ms: 0,
            isComplete: true,
        },
        {
            label: "PROCESSING",
            key: "processing",
            timestamp: log.processed_at ?? "",
            ms: base ? Math.max(0, processed - base) : 0,
            isComplete: !!log.processed_at,
        },
        {
            label: "API OUTBOUND",
            key: "outbound",
            timestamp: log.sent_at ?? "",
            ms: base ? Math.max(0, sent - base) : 0,
            isComplete: !!log.sent_at,
        },
        {
            label: "COMPLETED",
            key: "completed",
            timestamp: log.sent_at ?? log.processed_at ?? "",
            ms: base
                ? Math.max(
                      0,
                      (log.sent_at
                          ? new Date(log.sent_at).getTime()
                          : processed) - base,
                  )
                : 0,
            isComplete: !!log.sent_at || log.status === "SENT",
        },
    ];
}

/**
 * Total duration in seconds from received to sent (or last known step).
 */
export function getDurationSeconds(log: DebugLog): number {
    const received = log.received_at ? new Date(log.received_at).getTime() : 0;
    const sent = log.sent_at ? new Date(log.sent_at).getTime() : 0;
    if (!received || !sent) return 0;
    return Math.max(0, sent - received) / 1000;
}

/**
 * Relative time string (e.g. "Just now", "2 mins ago").
 */
export function formatRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMs < 60000) return "Just now";
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? "s" : ""} ago`;
    if (diffHours < 24)
        return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
}

export function formatShortId(leadgenId: string, maxLength = 10): string {
    if (!leadgenId) return "—";
    if (leadgenId.length <= maxLength) return leadgenId;
    return `${leadgenId.slice(0, 6)}...`;
}

/**
 * Format timestamp for display (e.g. "2026-01-19 - 06:26 AM")
 */
export function formatTime(dateStr: string): string {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = date.getHours();
    const mins = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    const hoursStr = String(hours12).padStart(2, "0");
    return `${year}-${month}-${day} / ${hoursStr}:${mins} ${ampm}`;
}

/**
 * Get status code from outgoing response
 */
export function getStatusCode(log: DebugLog): number | null {
    return log.outgoing_response?.response?.code ?? null;
}
